import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';

// ─────────────────────────────────────────────────────────────────────────────
// SCROLL REVEAL HOOK
// ─────────────────────────────────────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref     = useRef(null);
  const [v, sv] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) sv(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, v];
}

// ─────────────────────────────────────────────────────────────────────────────
// CLAUDE CONVERSATION DEMO
// Shows a realistic Claude.ai chat with ICE_Man MCP integration
// ─────────────────────────────────────────────────────────────────────────────
const CHAT_STEPS = [
  {
    delay:  0,
    role:   'user',
    text:   'Start a volume bot on BONK. Use 0.5 SOL.',
  },
  {
    delay:  1200,
    role:   'thinking',
    text:   'Calling ICE_Man → start_bot',
  },
  {
    delay:  2200,
    role:   'tool',
    lines: [
      { label: 'token',  val: 'BONK'            },
      { label: 'amount', val: '0.5 SOL'         },
      { label: 'route',  val: 'Jupiter V6'      },
      { label: 'status', val: '✓ Confirmed',  ok: true },
      { label: 'sig',    val: '4xK9...mN2p'    },
    ],
  },
  {
    delay:  3400,
    role:   'assistant',
    text:   'Done. Your BONK Volume Bot is live with 0.5 SOL. I\'ll alert you when it hits your profit target.',
  },
  {
    delay:  4800,
    role:   'user',
    text:   'Set an alert when BONK is up 15%.',
  },
  {
    delay:  5800,
    role:   'thinking',
    text:   'Calling ICE_Man → set_alert',
  },
  {
    delay:  6600,
    role:   'assistant',
    text:   'Alert set. I\'ll notify you the moment BONK reaches +15% from current price.',
  },
];

function ConversationDemo({ color }) {
  const [steps,    setSteps]   = useState([]);
  const [started,  setStarted] = useState(false);
  const containerRef           = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && !started) setStarted(true); },
      { threshold: 0.25 }
    );
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    setSteps([]);
    const timers = CHAT_STEPS.map(s =>
      setTimeout(() => setSteps(prev => [...prev, s]), s.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [started]);

  return (
    <div ref={containerRef} className="relative w-full max-w-xl mx-auto">
      {/* glow */}
      <div
        className="absolute -inset-10 blur-3xl opacity-10 pointer-events-none rounded-3xl"
        style={{ background: `radial-gradient(ellipse at 50% 50%, ${color}, transparent 70%)` }}
      />

      {/* window */}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#050505]">
        {/* title bar */}
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/6 bg-[#080808]">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-4 text-[11px] text-zinc-500 font-mono">Claude  ·  ICE_Man connected</span>
          <div className="ml-auto flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color, animation: 'pulse 1.8s ease-in-out infinite' }} />
            <span className="text-[10px] font-mono" style={{ color }}>MCP active</span>
          </div>
        </div>

        {/* chat area */}
        <div className="p-5 space-y-4 min-h-[320px]">
          {steps.map((step, i) => {
            if (step.role === 'user') {
              return (
                <div key={i} className="flex justify-end fade-up" style={{ animationDuration: '0.4s' }}>
                  <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-sm text-sm text-white bg-zinc-800">
                    {step.text}
                  </div>
                </div>
              );
            }
            if (step.role === 'thinking') {
              return (
                <div key={i} className="flex items-center gap-2 fade-up" style={{ animationDuration: '0.3s' }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center border border-white/20 shrink-0">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  </div>
                  <span className="text-[11px] font-mono" style={{ color, opacity: 0.7 }}>{step.text}</span>
                  <span className="flex gap-0.5">
                    {[0,1,2].map(d => (
                      <span key={d} className="w-1 h-1 rounded-full bg-zinc-600"
                        style={{ animation: `pulse 1.2s ${d * 0.2}s ease-in-out infinite` }}
                      />
                    ))}
                  </span>
                </div>
              );
            }
            if (step.role === 'tool') {
              return (
                <div key={i} className="fade-up ml-7" style={{ animationDuration: '0.4s' }}>
                  <div className="border border-white/8 bg-[#0a0a0a] rounded-xl p-4 font-mono text-xs space-y-2">
                    <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color, opacity: 0.6 }}>
                      ICE_Man response
                    </div>
                    {step.lines.map(({ label, val, ok }) => (
                      <div key={label} className="flex items-center justify-between gap-4">
                        <span className="text-zinc-600 shrink-0">{label}</span>
                        <span className={ok ? 'text-green-400' : 'text-zinc-300'}>{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            if (step.role === 'assistant') {
              return (
                <div key={i} className="flex gap-3 fade-up" style={{ animationDuration: '0.4s' }}>
                  <div
                    className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold mt-0.5"
                    style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
                  >
                    C
                  </div>
                  <div className="text-sm text-zinc-300 leading-relaxed max-w-[85%]">{step.text}</div>
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* input bar */}
        <div className="px-5 py-4 border-t border-white/6 flex items-center gap-3">
          <div className="flex-1 px-4 py-2.5 border border-white/8 rounded-xl bg-transparent text-zinc-600 text-sm font-mono">
            Message Claude...
          </div>
          <button
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: color }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7H13M13 7L8 2M13 7L8 12" stroke="#000" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TOOL CARD  — 5 MCP primitives
// ─────────────────────────────────────────────────────────────────────────────
function ToolCard({ cmd, title, desc, example, index, color }) {
  const [ref, visible] = useReveal();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      className="relative border border-white/8 bg-[#060606] p-6 transition-all duration-300 hover:-translate-y-1 group cursor-default"
      style={{
        opacity:         visible ? 1 : 0,
        transform:       visible ? (hovered ? 'translateY(-4px)' : 'translateY(0)') : 'translateY(20px)',
        transitionDelay: `${index * 80}ms`,
        boxShadow: hovered ? `0 0 40px ${color}12` : 'none',
        borderColor: hovered ? `${color}30` : 'rgba(255,255,255,0.08)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* command chip */}
      <div
        className="inline-block px-3 py-1 text-[10px] font-mono font-bold tracking-widest mb-5 rounded"
        style={{ backgroundColor: `${color}12`, color, border: `1px solid ${color}20` }}
      >
        {cmd}
      </div>

      <h3 className="font-display font-bold text-base tracking-tight mb-2">{title}</h3>
      <p className="text-zinc-500 text-sm leading-relaxed mb-5">{desc}</p>

      {/* example prompt */}
      <div className="border-t border-white/5 pt-4">
        <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-2">Example</div>
        <div className="font-mono text-xs text-zinc-400 leading-relaxed">"{example}"</div>
      </div>

      {/* hover glow edge */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px transition-opacity duration-300"
        style={{
          opacity: hovered ? 1 : 0,
          background: `linear-gradient(90deg, transparent, ${color}60, transparent)`,
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SETUP STEP
// ─────────────────────────────────────────────────────────────────────────────
function SetupStep({ num, title, body, code, index, color }) {
  const [ref, visible] = useReveal();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      ref={ref}
      className="flex gap-8 transition-all duration-700"
      style={{
        opacity:         visible ? 1 : 0,
        transform:       visible ? 'translateY(0)' : 'translateY(20px)',
        transitionDelay: `${index * 150}ms`,
      }}
    >
      {/* step number + vertical line */}
      <div className="flex flex-col items-center gap-0 shrink-0">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center font-display font-black text-sm shrink-0"
          style={{
            color,
            border:     `1.5px solid ${color}50`,
            background: `${color}08`,
          }}
        >
          {num}
        </div>
        {index < 2 && (
          <div className="w-px flex-1 mt-3 mb-0 min-h-[40px]" style={{ background: `${color}18` }} />
        )}
      </div>

      {/* content */}
      <div className="pb-12">
        <h3 className="font-display font-bold text-xl tracking-tight mb-3 mt-2">{title}</h3>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4 max-w-lg">{body}</p>

        {code && (
          <div className="flex items-center gap-3 border border-white/8 bg-[#060606] px-4 py-3 max-w-lg group/code">
            <code className="font-mono text-xs text-zinc-300 flex-1 truncate">{code}</code>
            <button
              onClick={handleCopy}
              className="shrink-0 text-[10px] font-mono uppercase tracking-widest transition-colors"
              style={{ color: copied ? '#4ade80' : color }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN ICEMAN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const MCP_URL = 'https://delightful-serenity-production-e319.up.railway.app/mcp';

const TOOLS = [
  {
    cmd:     'get_portfolio',
    title:   'View Portfolio',
    desc:    'Returns all active bots, current P&L, and available treasury balance as a live snapshot.',
    example: 'Show me my portfolio and today\'s earnings',
  },
  {
    cmd:     'start_bot',
    title:   'Deploy a Bot',
    desc:    'Launch a Volume, Sniper, or Lure Bot on any token with a specified SOL amount. Executes via Jupiter V6.',
    example: 'Start a Volume Bot on BONK with 0.5 SOL',
  },
  {
    cmd:     'stop_bot',
    title:   'Stop a Bot',
    desc:    'Immediately halt any active bot. Emergency stop with on-chain confirmation.',
    example: 'Stop my BONK bot right now',
  },
  {
    cmd:     'withdraw_profits',
    title:   'Withdraw Profits',
    desc:    'Move earned bot profits from the treasury to any external Solana wallet address.',
    example: 'Withdraw my profits to my Phantom wallet',
  },
  {
    cmd:     'set_alert',
    title:   'Set an Alert',
    desc:    'Trigger a notification when a token hits a price target or percentage move.',
    example: 'Alert me when WIF is up 20%',
  },
];

const SETUP_STEPS = [
  {
    num:   '1',
    title: 'Open Claude.ai Settings',
    body:  'In Claude.ai, go to Settings → Integrations → MCP Servers. This is where you connect external tools to your Claude account.',
    code:  null,
  },
  {
    num:   '2',
    title: 'Add the ICE_Man Server',
    body:  'Click "Add MCP Server" and paste the ICE_Man endpoint URL. Claude will automatically discover all 5 available tools.',
    code:  MCP_URL,
  },
  {
    num:   '3',
    title: 'Start trading',
    body:  'Open a new conversation in Claude.ai. Type any trade command in plain English — ICE_Man handles the rest, on-chain.',
    code:  null,
  },
];

function SecurityCard({ icon, title, body, index, color }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className="p-6 border border-white/8 bg-[#060606] transition-all duration-700"
      style={{
        opacity:         visible ? 1 : 0,
        transform:       visible ? 'translateY(0)' : 'translateY(16px)',
        transitionDelay: `${index * 100}ms`,
      }}
    >
      <div className="text-2xl mb-4" style={{ color, opacity: 0.7 }}>{icon}</div>
      <h3 className="font-display font-bold text-base tracking-tight mb-2">{title}</h3>
      <p className="text-zinc-500 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

export default function ICEMan() {
  const { theme }  = useTheme();
  const navigate   = useNavigate();
  const [heroIn, setHeroIn] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  const copyURL = () => {
    navigator.clipboard.writeText(MCP_URL);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      <Header />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-28 pb-24 text-center overflow-hidden">
        {/* ambient glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full blur-[200px]"
            style={{ background: theme.color, opacity: 0.05 }}
          />
        </div>
        <div className="grain absolute inset-0 opacity-25 pointer-events-none" />

        <div
          className="relative z-10 max-w-4xl mx-auto transition-all duration-1000 ease-out"
          style={{ opacity: heroIn ? 1 : 0, transform: heroIn ? 'translateY(0)' : 'translateY(24px)' }}
        >
          {/* eyebrow */}
          <div
            className="inline-flex items-center gap-3 mb-10 px-5 py-2 border"
            style={{ borderColor: `${theme.color}30`, background: `${theme.color}08` }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: theme.color, animation: 'pulse 1.8s ease-in-out infinite' }}
            />
            <span className="text-[11px] uppercase tracking-[0.28em] font-mono" style={{ color: theme.color }}>
              ICE_Man · MCP-native DeFi
            </span>
          </div>

          {/* headline */}
          <h1 className="font-display font-black text-[clamp(3.2rem,8vw,6.5rem)] leading-[0.88] tracking-tight mb-8">
            Trade with your voice.
            <br />
            <span style={{ color: theme.color }}>Execute on-chain.</span>
          </h1>

          <p className="text-zinc-400 text-lg sm:text-xl leading-relaxed mb-4 max-w-2xl mx-auto">
            ICE_Man is the world's first Model Context Protocol server for DeFi.
            Connect it to Claude.ai once — then control your entire Solana portfolio
            in plain English.
          </p>
          <p className="text-zinc-600 text-sm mb-14 max-w-xl mx-auto font-mono">
            No new apps. No dashboards. No code. Just conversation.
          </p>

          {/* MCP URL copy strip */}
          <div
            className="flex items-center justify-between gap-4 border border-white/10 bg-[#060606] px-5 py-3.5 max-w-lg mx-auto mb-10"
          >
            <span className="text-zinc-600 text-[11px] font-mono uppercase tracking-wider shrink-0">MCP URL</span>
            <span className="font-mono text-xs text-zinc-400 truncate flex-1 text-left">
              {MCP_URL}
            </span>
            <button
              onClick={copyURL}
              className="shrink-0 text-[10px] font-mono font-bold uppercase tracking-widest transition-all px-3 py-1.5 border"
              style={{
                color:        urlCopied ? '#4ade80' : theme.color,
                borderColor:  urlCopied ? '#4ade8040' : `${theme.color}40`,
              }}
            >
              {urlCopied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login?tier=tier_9')}
              className="px-10 py-4 font-display font-bold text-sm tracking-[0.2em] uppercase transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: theme.color,
                color: '#000',
                boxShadow: `0 0 48px ${theme.color}55`,
              }}
            >
              Upgrade to Power
            </button>
            <a
              href="#setup"
              className="px-10 py-4 font-display font-bold text-sm tracking-[0.2em] uppercase border border-white/12 text-zinc-300 hover:border-white/25 hover:text-white transition-all"
            >
              See Setup Guide
            </a>
          </div>
        </div>

        {/* scroll cue */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="w-px h-14 bg-gradient-to-b from-transparent to-white/20" />
          <span className="text-[9px] uppercase tracking-[0.3em] text-zinc-600">Scroll</span>
        </div>
      </section>

      {/* ── WHAT IS MCP ───────────────────────────────────────── */}
      <section className="py-36 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">The Protocol</div>
            <h2 className="font-display font-black text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[0.95] tracking-tight mb-8">
              What is MCP,<br />
              <span style={{ color: theme.color }}>in plain English?</span>
            </h2>

            <div className="space-y-6 max-w-lg">
              <p className="text-zinc-400 text-base leading-relaxed">
                MCP (Model Context Protocol) is an open standard that lets AI assistants 
                like Claude.ai connect to external tools and services — the same way your 
                phone connects to apps.
              </p>
              <p className="text-zinc-400 text-base leading-relaxed">
                ICE_Man is one of those tools. When you connect it, Claude gains the ability 
                to read your portfolio, deploy bots, withdraw profits, and set alerts — 
                all without leaving the chat window.
              </p>
              <p className="text-zinc-500 text-sm leading-relaxed">
                Think of it as giving Claude a direct line to your trading infrastructure.
                You speak. It executes. The blockchain confirms.
              </p>
            </div>
          </div>

          {/* architecture visual */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-sm space-y-3">
              {[
                { label: 'You',           sub: 'natural language',     icon: '◎' },
                { label: 'Claude.ai',     sub: 'AI reasoning',         icon: '◈', isAccent: true },
                { label: 'ICE_Man MCP',   sub: 'protocol bridge',      icon: '⟐', isAccent: true },
                { label: 'Jupiter V6',    sub: 'swap execution',        icon: '⬡' },
                { label: 'Solana',        sub: 'on-chain confirmation', icon: '✦' },
              ].map(({ label, sub, icon, isAccent }, i) => (
                <React.Fragment key={label}>
                  <div
                    className="flex items-center gap-5 px-5 py-4 border transition-all duration-300"
                    style={{
                      borderColor: isAccent ? `${theme.color}40` : 'rgba(255,255,255,0.07)',
                      background:  isAccent ? `${theme.color}06` : '#060606',
                    }}
                  >
                    <span className="text-xl shrink-0" style={{ color: isAccent ? theme.color : '#3f3f46' }}>
                      {icon}
                    </span>
                    <div>
                      <div className={`font-display font-bold text-sm ${isAccent ? '' : 'text-zinc-300'}`}
                        style={{ color: isAccent ? theme.color : undefined }}>
                        {label}
                      </div>
                      <div className="text-zinc-600 text-xs font-mono">{sub}</div>
                    </div>
                  </div>
                  {i < 4 && (
                    <div className="flex justify-center">
                      <div className="w-px h-3" style={{ background: `${theme.color}30` }} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 5 TOOLS ───────────────────────────────────────────── */}
      <section className="py-36 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">

          <div className="mb-20">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Capabilities</div>
            <h2 className="font-display font-black text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[0.95] tracking-tight">
              Five tools.<br />
              <span style={{ color: theme.color }}>Total control.</span>
            </h2>
            <p className="text-zinc-500 text-base mt-5 max-w-lg">
              Every trade action mapped to a natural language command.
              ICE_Man exposes 5 MCP primitives — together they cover the full DeFi lifecycle.
            </p>
          </div>

          {/* 5-card grid: 3 top + 2 centered bottom */}
          <div className="grid md:grid-cols-3 gap-5 mb-5">
            {TOOLS.slice(0, 3).map((tool, i) => (
              <ToolCard key={tool.cmd} {...tool} index={i} color={theme.color} />
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-2xl">
            {TOOLS.slice(3).map((tool, i) => (
              <ToolCard key={tool.cmd} {...tool} index={i + 3} color={theme.color} />
            ))}
          </div>
        </div>
      </section>

      {/* ── LIVE DEMO ─────────────────────────────────────────── */}
      <section className="py-36 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Live Demo</div>
            <h2 className="font-display font-black text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[0.95] tracking-tight mb-7">
              Watch it<br />
              <span style={{ color: theme.color }}>in action.</span>
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mb-6 max-w-md">
              A real conversation with Claude.ai, powered by ICE_Man. The user deploys 
              a Volume Bot on BONK and sets a price alert — entirely in natural language.
            </p>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-md">
              On-chain execution happens in under 5 seconds. The blockchain confirmation 
              is returned directly inside Claude's response.
            </p>
          </div>

          <ConversationDemo color={theme.color} />
        </div>
      </section>

      {/* ── SETUP GUIDE ───────────────────────────────────────── */}
      <section id="setup" className="py-36 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20">

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Get started</div>
            <h2 className="font-display font-black text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[0.95] tracking-tight mb-7">
              Connected in<br />
              <span style={{ color: theme.color }}>under 60 seconds.</span>
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-md">
              You need a Claude.ai Pro or Teams subscription and a GauriLabs Power tier account.
              That's it. No downloads, no browser extensions, no API keys to manage.
            </p>

            <div
              className="inline-flex items-center gap-3 px-5 py-3 border text-sm mb-8"
              style={{ borderColor: `${theme.color}30`, background: `${theme.color}06` }}
            >
              <span style={{ color: theme.color }}>✦</span>
              <span className="text-zinc-300">Requires Power tier · $499/month</span>
              <button
                onClick={() => navigate('/login?tier=tier_9')}
                className="ml-2 text-xs font-mono uppercase tracking-widest transition-opacity hover:opacity-70"
                style={{ color: theme.color }}
              >
                Upgrade →
              </button>
            </div>
          </div>

          <div className="pt-2">
            {SETUP_STEPS.map((step, i) => (
              <SetupStep key={step.num} {...step} index={i} color={theme.color} />
            ))}
          </div>
        </div>
      </section>

      {/* ── SECURITY ──────────────────────────────────────────── */}
      <section className="py-36 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">

          <div className="mb-16">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Security</div>
            <h2 className="font-display font-black text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[0.95] tracking-tight max-w-2xl">
              Built like infrastructure.
              <br />
              <span style={{ color: theme.color }}>Not like a startup project.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <SecurityCard index={0} color={theme.color} icon="⬡" title="HMAC Authentication"      body="Every MCP request is signed with HMAC-SHA256. Replay attacks and spoofed requests are blocked at the protocol level." />
            <SecurityCard index={1} color={theme.color} icon="◈" title="Non-Custodial Roadmap"    body="Anchor PDA architecture in progress. Future versions will sign all transactions directly from your wallet — zero custody." />
            <SecurityCard index={2} color={theme.color} icon="✦" title="No Private Key Exposure"  body="ICE_Man never holds or sees your private keys. Treasury operations are handled by a dedicated, isolated keypair." />
            <SecurityCard index={3} color={theme.color} icon="◎" title="Read-Only by Default"     body="Portfolio queries and alerts are fully read-only. Write operations require explicit command confirmation in your Claude chat." />
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────── */}
      <section className="relative py-40 px-6 border-t border-white/5 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at 50% 100%, ${theme.color}10, transparent 65%)` }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">Power Tier Exclusive</div>
          <h2 className="font-display font-black text-[clamp(3rem,6vw,5rem)] leading-[0.9] tracking-tight mb-8">
            Your AI is ready.<br />
            <span style={{ color: theme.color }}>Are you?</span>
          </h2>
          <p className="text-zinc-400 text-lg mb-14 max-w-md mx-auto leading-relaxed">
            ICE_Man is exclusive to Power tier. Upgrade now and deploy 
            your first bot in the next 5 minutes.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login?tier=tier_9')}
              className="px-12 py-5 font-display font-bold text-sm tracking-[0.2em] uppercase transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: theme.color,
                color: '#000',
                boxShadow: `0 0 64px ${theme.color}44`,
              }}
            >
              Upgrade to Power — $499/mo
            </button>
            <Link
              to="/#pricing"
              className="px-12 py-5 font-display font-bold text-sm tracking-[0.2em] uppercase border border-white/12 text-zinc-400 hover:text-white hover:border-white/25 transition-all"
            >
              Compare Plans
            </Link>
          </div>

          <p className="text-zinc-700 text-xs font-mono mt-8">
            14-day free trial · Cancel anytime · No credit card required
          </p>
        </div>
      </section>

      {/* ── MINIMAL FOOTER ────────────────────────────────────── */}
      <footer className="border-t border-white/5 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-display font-black text-lg tracking-tight" style={{ color: theme.color }}>
            GauriLabs
          </Link>
          <div className="flex items-center gap-8">
            {[['Home', '/'], ['Commander', '/dashboard'], ['Pricing', '/#pricing'], ['Login', '/login']].map(([label, href]) => (
              <Link key={label} to={href} className="text-zinc-500 text-sm hover:text-zinc-300 transition-colors">
                {label}
              </Link>
            ))}
          </div>
          <p className="text-zinc-700 text-xs font-mono">© 2026 Gaurisha Labs Pvt Ltd</p>
        </div>
      </footer>
    </div>
  );
}
